import { useAuth } from "@clerk/nextjs";
import { useMutation, useQuery, useQueryClient, UseQueryResult } from "@tanstack/react-query";
import { Invoice, ProductInOrder } from "~/lib/drizzle/type";
import { apiBaseUrl, fetchWrapper, parseQueryParams, constructUrlSearchParams } from "~/utils/utils";
import { PaginationResponse } from "~/server/repository/util";
import { useCallback, useMemo, useState, type ChangeEvent, type MouseEvent } from 'react';
import { omit } from "lodash";
import { BaseProps } from "./base";
import { useUserOrg } from "~/components/authguard";
import { generateInvoicePdfBuffer } from "~/utils/pdf/invoice";
import { InvoiceStatusEnum } from "~/lib/drizzle/invoice";
import { useGeneratePresignedUrl, useUploadFile } from "./file";
import toast from "react-hot-toast";

export const useGetInvoices = (params: GetInvoicesParams): UseQueryResult<PaginationResponse<Invoice>> => {
    const { getToken } = useAuth();
    const filteredParams = parseQueryParams(params);
    const queryFn = async () => {
        const token = await getToken();
        const result = await fetchWrapper<PaginationResponse<Invoice>>({
            url: `${apiBaseUrl}/invoices?${constructUrlSearchParams(filteredParams).toString()}`,
            method: 'GET',
            token
        });

        return result
    }

    return useQuery({
        queryKey: ['getInvoices', filteredParams],
        queryFn,
        staleTime: 1000,
        refetchOnWindowFocus: false
    })
}

export const useGetInvoiceSummary = (params: GetInvoicesParams): UseQueryResult<InvoiceSummary> => {
    const { getToken } = useAuth();
    const { page, limit, ...restParams } = params;
    const filteredParams = parseQueryParams(restParams);

    const queryFn = async () => {
        const token = await getToken();
        const result = await fetchWrapper<InvoiceSummary>({
            url: `${apiBaseUrl}/invoices/summary?${constructUrlSearchParams(filteredParams).toString()}`,
            method: 'GET',
            token
        });

        return result
    }

    return useQuery({
        queryKey: ['getInvoiceSummary', filteredParams],
        queryFn,
        staleTime: 1000,
        refetchOnWindowFocus: false
    })
}

interface ExportInvoicesParams extends BaseProps, GetInvoicesParams {
}

export const useExportInvoices = (props: ExportInvoicesParams) => {
    const { onSuccess, onError, ...restParams } = props;
    const { getToken } = useAuth();
    const { organization } = useUserOrg();
    const invoiceFilters = parseQueryParams(restParams);

    const mutationFn = async (params: ExportInvoicesParams): Promise<InvoicePDF[]> => {
        const token = await getToken();

        const filteredParams = parseQueryParams(params.invoice_ids?.length ? {
            invoice_ids: params.invoice_ids,
            page: 1,
            limit: params.invoice_ids?.length,
            with_products_in_order: true,
            with_paynow_qr: true
        } : {
            ...invoiceFilters,
            page: 1,
            limit: params.limit, // Export all selected by setting the limit same as total count
            with_products_in_order: true,
            with_paynow_qr: true
        });
        const result = await fetchWrapper<PaginationResponse<Invoice>>({
            url: `${apiBaseUrl}/invoices?${constructUrlSearchParams(filteredParams).toString()}`,
            method: 'GET',
            token
        });

        const pdfs: InvoicePDF[] = [];
        for (const invoice of result?.data ?? []) {
            const pdf = await generateInvoicePdfBuffer({
                invoice,
                userOrg: organization,
                products: invoice.order?.products_in_order as ProductInOrder[] ?? [],
            });
            pdfs.push({
                id: invoice.id,
                name: `${invoice.invoiceERP ?? invoice.ref}.pdf`,
                data: pdf
            });
        }

        return pdfs;
    }

    const mutation = useMutation({
        mutationFn,
        onSuccess: async (data, variables) => {
            onSuccess?.(data, variables);
        },
        onError: (error) => {
            onError?.(error);
        }
    })

    return mutation;
}

export const useUpdateInvoice = (props: BaseProps) => {
    const { onSuccess, onError } = props;
    const { getToken } = useAuth();
    const queryClient = useQueryClient();

    const mutationFn = async (payload: UpdateInvoicePayload): Promise<Invoice | null> => {
        const token = await getToken();
        const result = await fetchWrapper<Invoice>({
            url: `${apiBaseUrl}/invoices/${payload.id}`,
            method: 'PATCH',
            token,
            body: payload
        });

        return result
    }

    const mutation = useMutation({
        mutationFn,
        onSuccess: async (data, variables) => {
            await queryClient.invalidateQueries({ queryKey: ['getInvoices'] });
            if (variables.status) {
                await queryClient.invalidateQueries({ queryKey: ['getInvoiceSummary'] });
            }
            onSuccess?.(data, variables);
        },
        onError: (error) => {
            onError?.(error);
        }
    })

    return mutation;
}

interface BulkUpdateInvoicesParams extends BaseProps, GetInvoicesParams { }

export const useBulkUpdateInvoices = (props: BulkUpdateInvoicesParams) => {
    const { onSuccess, onError, ...restParams } = props;
    const { getToken } = useAuth();
    const queryClient = useQueryClient();
    const invoiceFilters = parseQueryParams(restParams);

    const mutationFn = async (payload: BulkUpdateInvoicesPayload): Promise<void> => {
        const token = await getToken();

        const payloadBody = payload.is_select_all ? {
            ...invoiceFilters,
            page: 1,
            limit: undefined,
            is_select_all: payload.is_select_all,
            update_status: payload.status
        } : {
            invoice_ids: payload.invoice_ids,
            update_status: payload.status
        }

        await fetchWrapper<void>({
            url: `${apiBaseUrl}/invoices/bulk`,
            method: 'PATCH',
            token,
            body: payloadBody
        });
    }

    const mutation = useMutation({
        mutationFn,
        onSuccess: async (data, variables) => {
            await Promise.all([
                queryClient.invalidateQueries({ queryKey: ['getInvoices'] }),
                queryClient.invalidateQueries({ queryKey: ['getInvoiceSummary'] })
            ])
            onSuccess?.(data, variables);
        },
        onError: (error) => {
            onError?.(error);
        }
    })

    return mutation;
}

interface BulkSendInvoicesParams extends ExportInvoicesParams { }

export const useBulkSendInvoices = (props: BulkSendInvoicesParams) => {
    const { onSuccess, onError } = props;
    const { getToken } = useAuth();
    const queryClient = useQueryClient();
    const exportInvoicesMutation = useExportInvoices(props);
    const uploadFileMutation = useUploadFile(props);
    const getPresignedUrlMutation = useGeneratePresignedUrl();

    const mutationFn = async (payload: BulkSendInvoicesParams): Promise<void> => {
        const token = await getToken();
        const invoices = await exportInvoicesMutation.mutateAsync(payload);
        const failedPdfs: InvoicePDF[] = [];
        let invoiceToast = toast.loading('Sending invoices...');
        let counter = 0;
        for (const invoice of invoices) {
            try {
                const invoicePdf = invoice.data;
                const formData = new FormData();
                const blob = new Blob([invoicePdf], { type: 'application/pdf' });
                formData.append('file', blob, `invoice-${invoice.id}.pdf`);
                formData.append('file_type', 'INVOICE_PDF');
                const uploadResponse = await uploadFileMutation.mutateAsync({ formData });
                const filename = uploadResponse.file_name.split('/').pop();

                const presignedUrlResponse = await getPresignedUrlMutation.mutateAsync({
                    file_name: filename,
                    file_type: 'INVOICE_PDF'
                });

                await fetchWrapper<void>({
                    url: `${apiBaseUrl}/invoices/send-invoice`,
                    method: 'POST',
                    token,
                    body: {
                        id: invoice.id,
                        attachment_url: presignedUrlResponse.presigned_url,
                        mimetype: 'application/pdf'
                    }
                });
                toast.dismiss(invoiceToast);
                counter++;
                invoiceToast = toast.loading(`Sending invoices... (${counter}/${invoices.length})`);
            } catch (error) {
                console.log('error', error);
                failedPdfs.push(invoice);
            }
        }

        if (failedPdfs.length > 0) {
            onError?.(failedPdfs);
            toast.dismiss(invoiceToast);
            toast.error('Failed to send invoices');
        } else {
            toast.dismiss(invoiceToast);
            toast.success('Successfully sent invoices')
        }
    }

    const mutation = useMutation({
        mutationFn,
        onSuccess: async (data, variables) => {
            await Promise.all([
                queryClient.invalidateQueries({ queryKey: ['getInvoices'] }),
                queryClient.invalidateQueries({ queryKey: ['getInvoiceSummary'] })
            ])
            onSuccess?.(data, variables);
        },
        onError: (error) => {
            onError?.(error);
        }
    })

    return mutation;
}

interface UpdateInvoicePayload {
    id: string;
    ref?: string;
    invoice_remarks?: string;
    invoice_erp?: string;
    payment_terms?: string;
    truck_no?: string;
    status?: InvoiceStatusEnum;
}

export interface InvoicePDF {
    id: string;
    name: string;
    data: Buffer;
}

interface ExportInvoicesParams {
    invoice_ids?: string[];
    limit?: number;
}

interface GetInvoicesParams {
    page?: number;
    limit?: number;
    status?: string;
    search?: string;
    search_by?: string;
    invoice_start_date?: Date;
    invoice_end_date?: Date;
    delivery_start_date?: Date;
    delivery_end_date?: Date;
    due_start_date?: Date;
    due_end_date?: Date;
    truck_no?: string;
    customer_ids?: string[];
}


export interface BulkUpdateInvoicesPayload {
    invoice_ids?: string[];
    is_select_all?: boolean;
    status: InvoiceStatusEnum;
}


interface InvoiceSummaryDetails {
    count: number;
    subtotal: number;
}

interface InvoiceSummary {
    total: InvoiceSummaryDetails;
    pending: InvoiceSummaryDetails;
    confirmed: InvoiceSummaryDetails;
}

export interface InvoiceFilters {
    customers: string[];
    team: string[];
    endDate?: Date;
    query?: string;
    startDate?: Date;
    deliveryDateFrom?: Date;
    deliveryDateTo?: Date;
    dueDateFrom?: Date;
    dueDateTo?: Date;
    status?: InvoiceStatusEnum;
    truckNo?: string;
    searchBy?: string;
}

export interface InvoicesSearchState {
    filters: InvoiceFilters;
    page: number;
    rowsPerPage: number;
}

const useInvoicesSearch = () => {
    const [state, setState] = useState<InvoicesSearchState>({
        filters: {
            customers: [],
            team: [],
            endDate: undefined,
            query: '',
            status: undefined,
            startDate: undefined,
            deliveryDateFrom: undefined,
            deliveryDateTo: undefined,
            searchBy: undefined
        },
        page: 0,
        rowsPerPage: 25,
    });

    const handleFilterChange = useCallback((filters: Partial<InvoiceFilters>): void => {
        setState((prevState) => ({
            ...prevState,
            filters: {
                ...prevState.filters,
                ...filters,
            },
            page: 0,
        }));
    }, []);

    const handlePageChange = useCallback((event: MouseEvent<HTMLButtonElement> | null, page: number): void => {
        setState((prevState) => ({
            ...prevState,
            page,
        }));
    }, []);

    const handleRowsPerPageChange = useCallback((event: ChangeEvent<HTMLInputElement>): void => {
        setState((prevState) => ({
            ...prevState,
            rowsPerPage: parseInt(event.target.value, 10),
        }));
    }, []);

    return {
        handleFilterChange,
        handlePageChange,
        handleRowsPerPageChange,
        state,
    };
};

export const useFetchInvoices = () => {
    const {
        state: searchState,
        handleFilterChange,
        handlePageChange,
        handleRowsPerPageChange,
    } = useInvoicesSearch();

    const invoiceFilters = {
        page: searchState.page + 1,
        limit: searchState.rowsPerPage,
        status: searchState.filters.status,
        search: searchState.filters.query,
        search_by: searchState.filters.searchBy,
        invoice_start_date: searchState.filters.startDate,
        invoice_end_date: searchState.filters.endDate,
        delivery_start_date: searchState.filters.deliveryDateFrom,
        delivery_end_date: searchState.filters.deliveryDateTo,
        due_start_date: searchState.filters.dueDateFrom,
        due_end_date: searchState.filters.dueDateTo,
        truck_no: searchState.filters.truckNo,
        customer_ids: searchState.filters.customers,
    }

    const isFilterActive = useMemo(() => {
        const relevantFilters = omit(invoiceFilters, ['page', 'limit', 'status', 'search', 'search_by']);
        return Object.values(relevantFilters).some((value) => {
            if (Array.isArray(value)) {
                return value.length > 0;
            }
            return !!value;
        })
    }, [invoiceFilters]);

    const getInvoicesQuery = useGetInvoices(invoiceFilters);
    const getInvoiceSummaryQuery = useGetInvoiceSummary(invoiceFilters);

    return {
        isFilterActive,
        invoiceFilters,
        getInvoicesQuery,
        getInvoiceSummaryQuery,
        searchState,
        handleFilterChange,
        handlePageChange,
        handleRowsPerPageChange,
    };
};